import React from "react";
import { Languages, Palette, Bell, LayoutGrid, Target } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { IntelligenceCard } from "@/components/cards/Cards";
import { IntegrationStatusPanel } from "@/components/common/IntegrationStatus";
import { Toggle, usePref } from "@/components/common/Toggle";
import { useLaunch } from "@/context/LaunchContext";
import { useI18n } from "@/i18n";

export default function Settings() {
  const { d, lang, setLang } = useI18n();
  const { isAdmin } = useLaunch();
  const [compact, setCompact] = usePref("sdip.display.compact", false);
  const [riskLabels, setRiskLabels] = usePref("sdip.market.risk", true);
  const [notifShell, setNotifShell] = usePref("sdip.notif.shell", true);

  return (
    <div data-testid="settings-page">
      <PageHeader title={d.nav.settings} subtitle={lang === "tr" ? "Görünüm, dil ve market gösterim tercihleri." : "Appearance, language and market display preferences."} />

      <div className="grid gap-6 lg:grid-cols-2">
        <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Languages className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Dil" : "Language"}</span>}>
          <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1" data-testid="settings-lang">
            {(["tr", "en"] as const).map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`settings-lang-${l}`}
                className={`rounded-lg px-5 py-2 text-sm font-semibold transition ${lang === l ? "bg-atlas/15 text-atlas-300" : "text-muted-foreground hover:text-foreground"}`}>
                {l === "tr" ? "Türkçe" : "English"}
              </button>
            ))}
          </div>
        </IntelligenceCard>

        <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Palette className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Görünüm" : "Appearance"}</span>}>
          <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <span className="text-sm text-foreground/90">{lang === "tr" ? "Koyu tema" : "Dark theme"}</span>
            <span className="rounded-full border border-atlas/25 bg-atlas/10 px-3 py-1 text-xs font-semibold text-atlas-300">{lang === "tr" ? "Her zaman açık" : "Always on"}</span>
          </div>
        </IntelligenceCard>

        <IntelligenceCard title={<span className="inline-flex items-center gap-2"><LayoutGrid className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Gösterim" : "Display"}</span>}>
          <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <span className="text-sm text-foreground/90">{lang === "tr" ? "Kompakt kartlar" : "Compact cards"}</span>
            <Toggle checked={compact} onChange={setCompact} testid="pref-compact" />
          </div>
        </IntelligenceCard>

        <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Target className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Market Gösterimi" : "Market Display"}</span>}>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <span className="text-sm text-foreground/90">{lang === "tr" ? "Risk etiketlerini göster" : "Show risk labels"}</span>
              <Toggle checked={riskLabels} onChange={setRiskLabels} testid="pref-risk" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <span className="flex items-center gap-2 text-sm text-foreground/90"><Bell className="h-4 w-4" /> {lang === "tr" ? "Bildirim kabuğu" : "Notification shell"}</span>
              <Toggle checked={notifShell} onChange={setNotifShell} testid="pref-notif-shell" />
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{lang === "tr" ? "Backend'e bağlı ayarlar, ilgili uç noktalar sağlandığında adaptör üzerinden etkinleşir." : "Backend-dependent settings activate via the adapter once endpoints are provided."}</p>
        </IntelligenceCard>
      </div>

      <div className="mt-6">
        <IntelligenceCard title={lang === "tr" ? "Sistem Bağlantısı" : "System Connection"}>
          <div className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="system-connection">
            <span className="flex items-center gap-2 text-sm text-foreground/90"><span className="h-2 w-2 rounded-full bg-atlas-400" /> {lang === "tr" ? "Gerçek veri bağlantısı aktif" : "Live data connection active"}</span>
            <span className="text-xs text-muted-foreground">{lang === "tr" ? "Son güncelleme: az önce" : "Last updated: just now"}</span>
          </div>
        </IntelligenceCard>
        {isAdmin && <div className="mt-6" data-testid="admin-integration-panel"><IntegrationStatusPanel /></div>}
      </div>
    </div>
  );
}
